import { Fragment, useEffect, useState } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Auth from '../utils/auth';
import { useUser } from '../context/UserContext';
import Logo from '../components/Logo';
import DEFAULT from '../assets/images/default.jpg';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

export default function Navbar() {
	const location = useLocation();
	const { user, isLoggedIn } = useUser();
	const [scrolled, setScrolled] = useState(false);
	const [navigation, setNavigation] = useState([
		{ name: 'About', href: '/about' },
		{ name: 'Services', href: '/services' },
		{ name: 'Service Areas', href: '/service-areas' },
		{ name: 'Resources', href: '/resources' },
		{ name: 'Why Gabe', href: '/why-choose-gabe' },
	]);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 8);
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	return (
		<Disclosure
			as='nav'
			className={classNames(
				'sticky top-0 z-50 transition-all duration-200',
				scrolled
					? 'bg-surface/85 backdrop-blur-md shadow-[0_1px_0_0_rgba(0,0,0,0.04)]'
					: 'bg-surface border-b border-ink-100/60',
			)}
		>
			{({ open }) => (
				<>
					<div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
						<div className='relative flex h-20 items-center justify-between'>
							<div className='flex items-center lg:hidden'>
								<Disclosure.Button className='relative inline-flex items-center justify-center rounded-md p-2 text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500'>
									<span className='sr-only'>Open main menu</span>
									{open ? (
										<XMarkIcon
											className='block h-6 w-6'
											aria-hidden='true'
										/>
									) : (
										<Bars3Icon
											className='block h-6 w-6'
											aria-hidden='true'
										/>
									)}
								</Disclosure.Button>
							</div>
							<div className='flex flex-1 items-center justify-center lg:justify-start'>
								<div className='flex flex-shrink-0 items-center'>
									<Logo height='h-12' />
								</div>
								<div className='hidden lg:ml-10 lg:block'>
									<div className='flex items-center space-x-6'>
										{navigation.map((item) => {
											const current = location.pathname.startsWith(item.href);
											return (
												<NavLink
													key={item.name}
													to={item.href}
													className={classNames(
														'text-sm font-medium tracking-wide transition-colors',
														current
															? 'text-primary-500'
															: 'text-ink-700 hover:text-primary-500',
													)}
												>
													{item.name}
												</NavLink>
											);
										})}
									</div>
								</div>
							</div>
							<div className='hidden lg:flex items-center gap-4'>
								<a
									href='tel:+14434690030'
									className='text-sm font-medium text-ink-700 hover:text-primary-500'
								>
									(443) 469-0030
								</a>
								<Link
									to='/contact'
									className='inline-flex items-center rounded-full bg-ink-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 transition-colors'
								>
									Free assessment
								</Link>
								{isLoggedIn ? (
									<Menu
										as='div'
										className='relative'
									>
										<div>
											<Menu.Button className='flex rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'>
												<span className='sr-only'>Open user menu</span>
												<img
													className='h-9 w-9 rounded-full object-cover border-2 border-ink-300 hover:border-primary-500 transition-colors'
													src={DEFAULT}
													alt='user'
												/>
											</Menu.Button>
										</div>
										<Transition
											as={Fragment}
											enter='transition ease-out duration-100'
											enterFrom='transform opacity-0 scale-95'
											enterTo='transform opacity-100 scale-100'
											leave='transition ease-in duration-75'
											leaveFrom='transform opacity-100 scale-100'
											leaveTo='transform opacity-0 scale-95'
										>
											<Menu.Items className='absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none'>
												<Menu.Item>
													{({ focus }) => (
														<span
															className={classNames(
																focus ? 'bg-ink-50' : '',
																'block px-4 py-2 text-sm text-ink-700',
															)}
														>
															{user?.name}
														</span>
													)}
												</Menu.Item>
												{user?.isAdmin && (
													<Menu.Item>
														{({ focus }) => (
															<Link
																to='/admin/crm'
																className={classNames(
																	focus ? 'bg-ink-50' : '',
																	'block px-4 py-2 text-sm text-ink-700',
																)}
															>
																Admin
															</Link>
														)}
													</Menu.Item>
												)}
												<Menu.Item>
													{({ focus }) => (
														<button
															onClick={() => Auth.logout()}
															className={classNames(
																focus ? 'bg-ink-50' : '',
																'block px-4 py-2 text-sm text-ink-700 w-full text-left',
															)}
														>
															Sign out
														</button>
													)}
												</Menu.Item>
											</Menu.Items>
										</Transition>
									</Menu>
								) : null}
							</div>
						</div>
					</div>

					<Disclosure.Panel className='lg:hidden border-t border-ink-100'>
						<div className='space-y-1 bg-surface px-4 py-3'>
							{navigation.map((item) => (
								<Disclosure.Button
									key={item.name}
									as='a'
									href={item.href}
									className='block rounded-lg px-3 py-2 text-base font-medium text-ink-700 hover:bg-primary-50 hover:text-primary-700'
								>
									{item.name}
								</Disclosure.Button>
							))}
							<Disclosure.Button
								as='a'
								href='/ask-gabe'
								className='block rounded-lg px-3 py-2 text-base font-medium text-ink-700 hover:bg-primary-50 hover:text-primary-700'
							>
								Ask Gabe
							</Disclosure.Button>

							<Disclosure.Button
								as='a'
								href='tel:+14434690030'
								className='block rounded-lg px-3 py-2 text-base font-semibold text-ink-700 hover:bg-primary-50 hover:text-primary-700'
							>
								(443) 469-0030
							</Disclosure.Button>
							<Disclosure.Button
								as='a'
								href='/contact'
								className='block rounded-full bg-ink-900 px-3 py-2.5 text-base font-semibold text-white hover:bg-primary-600 text-center mt-3'
							>
								Schedule a free assessment
							</Disclosure.Button>
						</div>
					</Disclosure.Panel>
				</>
			)}
		</Disclosure>
	);
}
